import { createRoute, type AnyRoute } from "@tanstack/react-router";
import { Binoculars } from "lucide-react";

import type { FeatureModule } from "@/features/types";
import { guarded } from "@/lib/auth";

import { CodeRagPage } from "./pages/CodeRagPage";

export type CodeRagTab = "overview" | "search" | "ask" | "rag-command" | "manage" | "how-to";

export interface CodeRagSearch {
  tab?: CodeRagTab;
  /** Repo filter, shared by the Search and Ask tabs. */
  repo?: string;
  /** Language filter, shared by the Search and Ask tabs. */
  lang?: string;
  /** Last query, so a search survives a reload and can be linked to. */
  q?: string;
}

const TABS: readonly CodeRagTab[] = [
  "overview",
  "search",
  "ask",
  "rag-command",
  "manage",
  "how-to",
];

function validateCodeRagSearch(search: Record<string, unknown>): CodeRagSearch {
  const out: CodeRagSearch = {};
  if (TABS.includes(search.tab as CodeRagTab)) out.tab = search.tab as CodeRagTab;
  if (typeof search.repo === "string" && search.repo) out.repo = search.repo;
  if (typeof search.lang === "string" && search.lang) out.lang = search.lang;
  if (typeof search.q === "string" && search.q) out.q = search.q;
  return out;
}

/**
 * Code RAG — the semantic index over Cinch's product repos, surfaced in the app.
 *
 * Distinct from the Code Base monitor next to it: that one is about repository
 * health (branches, PRs, CI), this one is about the *contents* of the code and
 * how current the index of it is.
 *
 * `code_rag.read` is granted directly, never via a "Monitors" category grant —
 * search results carry code bodies, so reading this is effectively source
 * access. `code_rag.admin` (the Manage Qdrant tab) is outside read/write
 * entirely, so no category grant can ever confer it. See
 * CATEGORY_GRANT_EXEMPT in backend/app/core/permissions/categories.py.
 */
export const codeRagModule: FeatureModule = {
  key: "code_rag",
  nav: {
    label: "Code RAG",
    icon: Binoculars,
    to: "/code-rag",
    requires: "code_rag.read",
    order: 41, // directly after Code Base (40)
    group: "Monitors",
  },
  routes: (rootRoute) =>
    [
      createRoute({
        getParentRoute: () => rootRoute,
        path: "/code-rag",
        component: guarded("code_rag.read", CodeRagPage),
        validateSearch: validateCodeRagSearch,
      }),
    ] as unknown as AnyRoute[],
};
